// Detects a connected WebRTC video receiver that is not producing usable
// frames, from successive `inbound-rtp` (kind "video") getStats() samples.
//
// Each condition below compares two consecutive samples. A condition must hold
// continuously for `stallMs` before the receiver is reported as stalled, so a
// single lost keyframe that is recovered by the next IDR does not trigger it.
//
//   received-not-decoded   framesReceived grows, framesDecoded does not:
//                          the decoder rejects what arrives.
//   packets-not-assembled  bytes/packets grow, framesReceived does not:
//                          packets never form complete frames.
//   decoded-all-dropped    every decoded frame is dropped before rendering.
//   frozen                 the renderer records freeze time in every interval.
//   keyframe-requests-unanswered
//                          pliCount/firCount grow, keyFramesDecoded does not:
//                          decoder sync is lost and no IDR is recovering it.
//   no-keyframe            media arrives but no keyframe was ever decoded.
//
// Counters a browser does not report are skipped rather than treated as zero.

export const DECODE_STALL_CONDITIONS = Object.freeze([
    "received-not-decoded",
    "packets-not-assembled",
    "decoded-all-dropped",
    "frozen",
    "keyframe-requests-unanswered",
    "no-keyframe",
]);

const SAMPLE_FIELDS = [
    "bytesReceived",
    "packetsReceived",
    "framesReceived",
    "framesDecoded",
    "framesDropped",
    "keyFramesDecoded",
    "freezeCount",
    "totalFreezesDuration",
    "pliCount",
    "firCount",
];

const isCounter = (value) => typeof value === "number" && Number.isFinite(value);

// Pick the receiving video stream's counters from an RTCStatsReport.
export const pickVideoInboundSample = (report) => {
    let entry = null;
    report.forEach((stats) => {
        if (stats.type === "inbound-rtp" && (stats.kind || stats.mediaType) === "video") entry = stats;
    });
    if (!entry) return null;
    const sample = { decoderImplementation: entry.decoderImplementation || null };
    SAMPLE_FIELDS.forEach((field) => {
        sample[field] = isCounter(entry[field]) ? entry[field] : null;
    });
    return sample;
};

const delta = (current, previous, field) => {
    if (!isCounter(current[field]) || !isCounter(previous[field])) return null;
    return current[field] - previous[field];
};

const grew = (value) => value !== null && value > 0;
const flat = (value) => value !== null && value <= 0;

// Conditions that hold between two consecutive samples.
export const activeConditions = (previous, current) => {
    const bytes = delta(current, previous, "bytesReceived");
    const packets = delta(current, previous, "packetsReceived");
    const received = delta(current, previous, "framesReceived");
    const decoded = delta(current, previous, "framesDecoded");
    const dropped = delta(current, previous, "framesDropped");
    const keyframes = delta(current, previous, "keyFramesDecoded");
    const freezeSeconds = delta(current, previous, "totalFreezesDuration");
    const pli = delta(current, previous, "pliCount");
    const fir = delta(current, previous, "firCount");
    const mediaArriving = grew(bytes) || grew(packets);

    const conditions = [];
    if (grew(received) && flat(decoded)) conditions.push("received-not-decoded");
    if (mediaArriving && flat(received)) conditions.push("packets-not-assembled");
    if (grew(decoded) && grew(dropped) && dropped >= decoded) conditions.push("decoded-all-dropped");
    if (grew(freezeSeconds)) conditions.push("frozen");
    if ((grew(pli) || grew(fir)) && flat(keyframes)) conditions.push("keyframe-requests-unanswered");
    if (mediaArriving && current.keyFramesDecoded === 0) conditions.push("no-keyframe");
    return conditions;
};

// Pure state transition. `state` is null for a new receiver.
// Returns { state, stalled, reasons, decoding }:
//   stalled   conditions that have held for at least stallMs;
//   decoding  frames were decoded with no condition active in this interval.
export const evaluateDecodeStall = (state, sample, nowMs, { stallMs }) => {
    if (!sample) return { state, stalled: [], reasons: [], decoding: false };
    if (!state) return { state: { sample, since: {} }, stalled: [], reasons: [], decoding: false };

    const reasons = activeConditions(state.sample, sample);
    const since = {};
    reasons.forEach((reason) => {
        since[reason] = state.since[reason] ?? nowMs;
    });
    const stalled = reasons.filter((reason) => nowMs - since[reason] >= stallMs);
    const decoding = reasons.length === 0 && grew(delta(sample, state.sample, "framesDecoded"));
    return { state: { sample, since }, stalled, reasons, decoding };
};
